/**
 * The parts of a field's configuration that decide how a value *reads*.
 *
 * Separate from the field types that use them because two things want them: the
 * type itself, and a formula that turns out to produce one. A formula returning
 * a date is a date as far as reading it goes, and the backend stores the same
 * five properties on it -- so the alternative to sharing these is a second set
 * of controls that has to be kept saying the same thing.
 *
 * Typed against the properties they touch rather than against a field type,
 * which is what lets one component serve a `number` field and a `formula` field
 * whose result is a number, without either of them knowing about the other.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TZDate } from '@date-fns/tz'

import type { DateFormat, TimeFormat } from '@/api/types'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { DATE_FORMATS, TIME_FORMATS } from '../../dates'
import {
  DURATION_FORMATS,
  formatDurationValue,
  type DurationFormat,
} from '../../duration'
import {
  NUMBER_MAX_DECIMAL_PLACES,
  NUMBER_SEPARATOR_FORMATS,
  formatNumberValue,
  parseNumberValue,
  type NumberSeparatorFormat,
} from '../../numbers'
import { Setting, Toggle } from './controls'

/**
 * The looser side of the seam these components sit on.
 *
 * Each declares the properties it touches in its own terms -- a decimal place
 * count is a number here, and one of eleven literals on a number field but one
 * of eleven-or-null on a formula. Widening the field types to match would make
 * them accept values the API refuses; narrowing these to match would stop them
 * being shared. So a caller adapts, which is what `changing` is for: it is a
 * cast, named, in one place, rather than six unexplained ones.
 */
export function changing<Wide, Narrow>(
  onChange: (changes: Narrow) => void,
): (changes: Wide) => void {
  return (changes) => {
    onChange(changes as unknown as Narrow)
  }
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

export interface NumberFormattingProperties {
  number_decimal_places?: number | null
  number_prefix?: string
  number_suffix?: string
  number_separator?: string
}

const DECIMAL_PLACES = Array.from(
  { length: NUMBER_MAX_DECIMAL_PLACES + 1 },
  (_unused, places) => places,
)

/** How each punctuation choice reads, as the same number written that way. */
const SEPARATOR_EXAMPLE: Record<NumberSeparatorFormat, string> = {
  '': '1000000.00',
  SPACE_COMMA: '1 000 000,00',
  SPACE_PERIOD: '1 000 000.00',
  COMMA_PERIOD: '1,000,000.00',
  PERIOD_COMMA: '1.000.000,00',
}

/** The longest prefix or suffix the column will hold. */
const AFFIX_MAX_LENGTH = 10

export function NumberFormatting({
  properties,
  onChange,
}: {
  properties: NumberFormattingProperties
  onChange: (changes: NumberFormattingProperties) => void
}) {
  const { t } = useTranslation()
  const places = useId()
  const separator = useId()
  const prefix = useId()
  const suffix = useId()

  return (
    <>
      <Setting id={places} label={t('fieldConfig.decimalPlaces')}>
        <NativeSelect
          id={places}
          className="w-full"
          value={String(properties.number_decimal_places ?? 0)}
          onChange={(event) => {
            onChange({ number_decimal_places: Number(event.target.value) })
          }}
        >
          {DECIMAL_PLACES.map((count) => (
            <NativeSelectOption key={count} value={count}>
              {count === 0 ? '1' : `1.${'0'.repeat(count)}`}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Setting>

      <Setting id={separator} label={t('fieldConfig.separator')}>
        <NativeSelect
          id={separator}
          className="w-full"
          value={properties.number_separator ?? ''}
          onChange={(event) => {
            onChange({ number_separator: event.target.value })
          }}
        >
          {NUMBER_SEPARATOR_FORMATS.map((format) => (
            <NativeSelectOption key={format} value={format}>
              {format === '' ? t('fieldConfig.separatorNone') : SEPARATOR_EXAMPLE[format]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Setting>

      <div className="flex gap-2">
        <Setting id={prefix} label={t('fieldConfig.prefix')}>
          <Input
            id={prefix}
            value={properties.number_prefix ?? ''}
            maxLength={AFFIX_MAX_LENGTH}
            onChange={(event) => {
              onChange({ number_prefix: event.target.value })
            }}
          />
        </Setting>
        <Setting id={suffix} label={t('fieldConfig.suffix')}>
          <Input
            id={suffix}
            value={properties.number_suffix ?? ''}
            maxLength={AFFIX_MAX_LENGTH}
            onChange={(event) => {
              onChange({ number_suffix: event.target.value })
            }}
          />
        </Setting>
      </div>
    </>
  )
}

/**
 * The value a new row starts with, written in the field's own punctuation.
 *
 * Stored as a plain decimal string -- a period for the mark, no grouping, no
 * prefix -- and shown the way the field shows a cell, so the box has to be read
 * one way and written the other.
 *
 * Two separate moments, and they are not the same one. Every keystroke is read
 * and stored, so the value is in the draft whether or not the box is left --
 * pressing return saves what is on screen. The *rendering* only catches up when
 * the box loses focus, because grouping marks appearing between keystrokes move
 * the cursor out from under the person typing.
 */
export function NumberDefault({
  properties,
  stored,
  onChange,
}: {
  properties: NumberFormattingProperties
  stored: string | null
  onChange: (value: string | null) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const [typed, setTyped] = useState<string | null>(null)

  const shape = properties as Parameters<typeof formatNumberValue>[0]
  const shown = typed ?? (stored === null ? '' : (formatNumberValue(shape, stored) ?? ''))

  return (
    <Setting id={id} label={t('fieldConfig.defaultValue')}>
      <Input
        id={id}
        value={shown}
        inputMode="decimal"
        placeholder={t('fieldConfig.defaultNumberPlaceholder')}
        onChange={(event) => {
          setTyped(event.target.value)
          const parsed = parseNumberValue(shape, event.target.value)
          // Null rather than the text: a default nothing can read as a number is
          // no default, and storing what was typed would send the backend a word
          // to put in a decimal column.
          onChange(parsed === null ? null : parsed.toString())
        }}
        onBlur={() => {
          setTyped(null)
        }}
      />
    </Setting>
  )
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

export interface DateFormattingProperties {
  date_format?: DateFormat | null
  date_include_time?: boolean | null
  date_time_format?: TimeFormat | null
  date_show_tzinfo?: boolean | null
  date_force_timezone?: string | null
  date_force_timezone_offset?: number | null
}

/**
 * Every timezone the platform knows, which is the same list the backend accepts.
 *
 * A plain list rather than the searchable paged dropdown upstream builds: four
 * hundred options is a long menu, but every browser types ahead through one, and
 * it cannot produce a name the backend would refuse.
 */
const TIME_ZONES: readonly string[] = Intl.supportedValuesOf('timeZone')

/** Minutes ahead of UTC, which is the sign the API's offset is expressed in. */
function utcOffset(timeZone: string, at: Date): number {
  return -new TZDate(at, timeZone).getTimezoneOffset()
}

/** The zone somebody is reading in when the field does not pin one. */
export const viewerTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

/** One moment, shown in each format, so a choice is read rather than decoded. */
const EXAMPLE = new Date(Date.UTC(2026, 1, 20, 23, 0))

export function DateFormatting({
  properties,
  onChange,
  /**
   * The zone the field's values are read in today, or null when they follow
   * whoever is looking. Undefined when there is nothing to convert -- a field
   * with no values of its own, or one the backend writes.
   */
  convertingFrom,
}: {
  properties: DateFormattingProperties
  onChange: (changes: DateFormattingProperties) => void
  convertingFrom: string | null | undefined
}) {
  const { t } = useTranslation()
  const format = useId()
  const includeTime = useId()
  const timeFormat = useId()
  const showZone = useId()
  const forceZone = useId()
  const zone = useId()
  const shift = useId()

  const forced = properties.date_force_timezone ?? null
  const withTime = properties.date_include_time === true

  // How far the values would move if they were left where they are: the offset
  // between the zone they are read in now and the one being pinned.
  const at = new Date()
  const offsetDiff =
    forced === null || convertingFrom === undefined
      ? 0
      : utcOffset(convertingFrom ?? viewerTimeZone(), at) - utcOffset(forced, at)

  return (
    <>
      <Setting id={format} label={t('fieldConfig.dateFormat')}>
        <NativeSelect
          id={format}
          className="w-full"
          value={properties.date_format ?? 'EU'}
          onChange={(event) => {
            onChange({ date_format: event.target.value as DateFormat })
          }}
        >
          {(Object.keys(DATE_FORMATS) as DateFormat[]).map((name) => (
            <NativeSelectOption key={name} value={name}>
              {t(`fieldConfig.dateFormatName.${name}`)} ({DATE_FORMATS[name].humanFormat})
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Setting>

      <Toggle
        id={includeTime}
        label={t('fieldConfig.includeTime')}
        checked={withTime}
        onChange={(date_include_time) => {
          onChange({ date_include_time })
        }}
      />

      {withTime && (
        <>
          <Setting id={timeFormat} label={t('fieldConfig.timeFormat')}>
            <NativeSelect
              id={timeFormat}
              className="w-full"
              value={properties.date_time_format ?? '24'}
              onChange={(event) => {
                onChange({ date_time_format: event.target.value as TimeFormat })
              }}
            >
              {(Object.keys(TIME_FORMATS) as TimeFormat[]).map((name) => (
                <NativeSelectOption key={name} value={name}>
                  {t(`fieldConfig.timeFormatName.${name}`)} (
                  {new Intl.DateTimeFormat('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: TIME_FORMATS[name].hour12,
                    timeZone: 'UTC',
                  }).format(EXAMPLE)}
                  )
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Setting>

          <Toggle
            id={forceZone}
            label={t('fieldConfig.forceTimezone')}
            hint={t('fieldConfig.forceTimezoneHint')}
            checked={forced !== null}
            onChange={(on) => {
              onChange({
                date_force_timezone: on ? viewerTimeZone() : null,
                // The instruction to move the values belongs to a zone. Left
                // behind when the zone is cleared, it would shift every value by
                // an amount that no longer means anything.
                date_force_timezone_offset: null,
              })
            }}
          />

          {forced !== null && (
            <Setting id={zone} label={t('fieldConfig.timezone')}>
              <NativeSelect
                id={zone}
                className="w-full"
                value={forced}
                onChange={(event) => {
                  onChange({
                    date_force_timezone: event.target.value,
                    // Same reason, one step along: the amount to move by was
                    // worked out against the zone that has just changed.
                    date_force_timezone_offset: null,
                  })
                }}
              >
                {TIME_ZONES.map((name) => (
                  <NativeSelectOption key={name} value={name}>
                    {name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Setting>
          )}

          {offsetDiff !== 0 && (
            <Toggle
              id={shift}
              label={t(
                offsetDiff > 0 ? 'fieldConfig.shiftForward' : 'fieldConfig.shiftBack',
                { minutes: Math.abs(offsetDiff) },
              )}
              hint={t('fieldConfig.shiftHint')}
              checked={properties.date_force_timezone_offset != null}
              onChange={(on) => {
                onChange({ date_force_timezone_offset: on ? offsetDiff : null })
              }}
            />
          )}

          <Toggle
            id={showZone}
            label={t('fieldConfig.showTimezone')}
            checked={properties.date_show_tzinfo === true}
            onChange={(date_show_tzinfo) => {
              onChange({ date_show_tzinfo })
            }}
          />
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Durations
// ---------------------------------------------------------------------------

export interface DurationFormattingProperties {
  duration_format?: DurationFormat | null
}

/**
 * One duration, shown in every format, as the label for each.
 *
 * Rendered rather than written down: a hand-kept table of examples is a second
 * statement of what the formatter does, and the two drift. A day, two hours,
 * three minutes and forty and a half seconds reaches every part of every format.
 */
const EXAMPLE_SECONDS = 26 * 3600 + 3 * 60 + 40.5

export function DurationFormatting({
  properties,
  onChange,
}: {
  properties: DurationFormattingProperties
  onChange: (changes: DurationFormattingProperties) => void
}) {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Setting
      id={id}
      label={t('fieldConfig.durationFormat')}
      hint={t('fieldConfig.durationFormatHint')}
    >
      <NativeSelect
        id={id}
        className="w-full"
        value={properties.duration_format ?? DURATION_FORMATS[0]}
        onChange={(event) => {
          onChange({ duration_format: event.target.value as DurationFormat })
        }}
      >
        {DURATION_FORMATS.map((format) => (
          <NativeSelectOption key={format} value={format}>
            {format} ({formatDurationValue(EXAMPLE_SECONDS, format)})
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Setting>
  )
}
